import {
  EnumExamDetectionResultsDetectionsAnoscopyGeniusHistologicalResult as EnumAnoscopyGeniusHistologicalResult,
  EnumExamDetectionResultsDetectionsClass,
  EnumExamDetectionResultsDetectionsSmartCystHistologicalResult as EnumSmartCystHistologicalResult,
  EnumExamDetectionResultsDetectionsSmartPancreasHistologicalResult as EnumSmartPancreasHistologicalResult,
} from 'generated/graphql';
export { EnumExamStatus as ExamStatus } from 'generated/graphql';

export enum FindingPriority {
  High = 'high',
  Medium = 'medium',
  Low = 'low',
}

export const ExamDetectionClassLabels: Record<
  EnumExamDetectionResultsDetectionsClass,
  string
> = {
  [EnumExamDetectionResultsDetectionsClass.Lesion]: 'Lesion',
  // Smart Cholangio
  [EnumExamDetectionResultsDetectionsClass.Benign]: 'Benign',
  [EnumExamDetectionResultsDetectionsClass.Malignant]: 'Malignant',
  // Smart Cyst
  [EnumExamDetectionResultsDetectionsClass.Mucinous]: 'Mucinous',
  [EnumExamDetectionResultsDetectionsClass.Nonmucinous]: 'Non-Mucinous',
  // Smart Pancreas
  [EnumExamDetectionResultsDetectionsClass.Adc]: 'Adenocarcinoma',
  [EnumExamDetectionResultsDetectionsClass.Net]: 'Neuroendocrine Tumor',
  // Anoscopy Genius
  [EnumExamDetectionResultsDetectionsClass.Hsil]: 'HSIL',
  [EnumExamDetectionResultsDetectionsClass.Lsil]: 'LSIL',
};

export const SmartCystHistologicalResultDisplayValues: Record<
  EnumSmartCystHistologicalResult,
  { label: string; tooltip?: string }
> = {
  [EnumSmartCystHistologicalResult.Mucinous]: {
    label: 'Mucinous',
    tooltip: 'Mucinous Pancreatic Cystic Lesion',
  },
  [EnumSmartCystHistologicalResult.MucinousIpmnSb]: {
    label: 'IPMN - SB',
    tooltip: 'Intraductal Papillary Mucinous Neoplasm of the Side Branch',
  },
  [EnumSmartCystHistologicalResult.MucinousIpmnSbHigh]: {
    label: 'IPMN - SB - High Grade',
    tooltip:
      'Intraductal Papillary Mucinous Neoplasm of the Side Branch with High-Grade Epithelial Dysplasia',
  },
  [EnumSmartCystHistologicalResult.MucinousIpmnSbLow]: {
    label: 'IPMN - SB - Low Grade',
    tooltip:
      'Intraductal Papillary Mucinous Neoplasm of the Side Branch with Low-Grade Epithelial Dysplasia',
  },
  [EnumSmartCystHistologicalResult.MucinousIpmnMd]: {
    label: 'IPMN - MD',
    tooltip: 'Intraductal Papillary Mucinous Neoplasm of the Main Duct',
  },
  [EnumSmartCystHistologicalResult.MucinousIpmnMdHigh]: {
    label: 'IPMN - MD - High Grade',
    tooltip:
      'Intraductal Papillary Mucinous Neoplasm of the Main Duct with High-Grade Epithelial Dysplasia',
  },
  [EnumSmartCystHistologicalResult.MucinousIpmnMdLow]: {
    label: 'IPMN - MD - Low Grade',
    tooltip:
      'Intraductal Papillary Mucinous Neoplasm of the Main Duct with Low-Grade Epithelial Dysplasia',
  },
  [EnumSmartCystHistologicalResult.MucinousMcn]: {
    label: 'MCN',
    tooltip: 'Mucinous Cystic Neoplasm',
  },
  [EnumSmartCystHistologicalResult.NonMucinous]: {
    label: 'Non-Mucinous',
    tooltip: 'Non-Mucinous Pancreatic Cystic Lesion',
  },
  [EnumSmartCystHistologicalResult.NonMucinousNetCysticComponent]: {
    label: 'NET Cystic Component',
    tooltip: 'Neuroendocrine Tumor with Cystic Component',
  },
  [EnumSmartCystHistologicalResult.NonMucinousPseudocyst]: {
    label: 'Pseudocyst',
    tooltip: 'Pancreatic Pseudocyst',
  },
  [EnumSmartCystHistologicalResult.NonMucinousSerousCystadenoma]: {
    label: 'Serous Cystadenoma',
  },
  [EnumSmartCystHistologicalResult.NonMucinousSolidPseudopapillaryNeoplasm]: {
    label: 'Solid Pseudopapillary Neoplasm',
  },
  [EnumSmartCystHistologicalResult.NonMucinousWalledOffPancreaticNecrosis]: {
    label: 'Walled-off Pancreatic Necrosis',
  },
};

export const SmartPancreasHistologicalResultLabels: Record<
  EnumSmartPancreasHistologicalResult,
  string
> = {
  [EnumSmartPancreasHistologicalResult.NeuroendocrineTumor]:
    'Neuroendocrine Tumor',
  [EnumSmartPancreasHistologicalResult.Adenocarcinoma]: 'Adenocarcinoma',
  [EnumSmartPancreasHistologicalResult.Metastasis]: 'Metastasis',
  [EnumSmartPancreasHistologicalResult.Cholangiocarcinoma]: 'Cholangiocarcinoa',
  [EnumSmartPancreasHistologicalResult.PseudopapillarySolidLesion]:
    'Pseudopapillary Solid Lesion',
  [EnumSmartPancreasHistologicalResult.StromalTumor]: 'Stromal Tumor',
  [EnumSmartPancreasHistologicalResult.AccessorySpleen]: 'Accessory Spleen',
  [EnumSmartPancreasHistologicalResult.Plasmacytoma]: 'Plasmacytoma',
  [EnumSmartPancreasHistologicalResult.InflammatoryMass]: 'Inflammatory Mass',
};

export const AnoscopyGeniusHistologicalResultDisplayValues: Record<
  EnumAnoscopyGeniusHistologicalResult,
  { label: string; tooltip?: string }
> = {
  [EnumAnoscopyGeniusHistologicalResult.Hsil]: {
    label: 'HSIL',
    tooltip: 'High-Grade Squamous Intraepithelial Lesion',
  },
  [EnumAnoscopyGeniusHistologicalResult.Lsil]: {
    label: 'LSIL',
    tooltip: 'Low-Grade Squamous Intraepithelial Lesion',
  },
  [EnumAnoscopyGeniusHistologicalResult.NonDysplasticLesion]: {
    label: 'Non-Dysplastic Lesion',
  },
  [EnumAnoscopyGeniusHistologicalResult.Cancer]: {
    label: 'Cancer',
  },
  [EnumAnoscopyGeniusHistologicalResult.Normal]: {
    label: 'Normal',
  },
};
